import { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import {
  AlertOutlinedIcon,
  BlockOutlinedIcon,
  LockedOutlinedIcon,
  SignOutOutlinedIcon,
} from '@getgo/chameleon-icons/react'
import { Button, EmptyState, GotoLogo } from '@getgo/chameleon-web-react-wrapper'
import { AppBar, makeStyles, Theme, Toolbar } from '@material-ui/core'

import GoToAdminDark from 'assets/GoTo-dark.svg'
import GoToAdminLight from 'assets/GoToAdmin.svg'
import { localStorageCleanupAndDeleteSelectedAccountKey } from 'utils'
import { ErrorCode } from './contexts/AppErrorContext'
import { auth } from './providers/GetGoAuthProvider'

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    borderBottom: `1px solid ${tokens.interactiveColorNeutralDefault}`,
    zIndex: theme.zIndex.drawer + 1,
    top: 0,
  },
}))

interface ErrorMessage {
  title: string
  subtitle: string
  icon: ReactNode
  action?: ReactNode
}

export interface ErrorPageProps {
  errorCode: ErrorCode
  chameleonTheme?: 'light' | 'dark'
  setError?: Dispatch<SetStateAction<string | undefined>>
}
const ErrorPage: FC<ErrorPageProps> = ({ chameleonTheme, errorCode }) => {
  const t = useTranslate()
  const [loggingOut, setLoggingOut] = useState<boolean>(false)
  const classes = useStyles()

  const logout = () => {
    setLoggingOut(true)
    localStorageCleanupAndDeleteSelectedAccountKey()
    auth.logout()
  }

  const errorMap: { [key: string]: ErrorMessage } = {
    'mobile-access': {
      title: t('GoTo Admin is not supported on mobile devices'),
      subtitle: t('Try again from a desktop browser.'),
      icon: <BlockOutlinedIcon />,
    },
    'account-access': {
      title: t("You don't have access to this account"),
      subtitle: t('Ask your admin for access, or sign in with a different user identity.'),
      action: (
        <Button variant="neutral" leadingIcon={<SignOutOutlinedIcon />} onClick={logout} disabled={loggingOut}>
          {t('Sign out')}
        </Button>
      ),
      icon: <LockedOutlinedIcon />,
    },
    'unauthorized-email': {
      title: t('Access denied'),
      subtitle: t('Unauthorized email.'),
      icon: <LockedOutlinedIcon />,
    },
    'unexpected-error': {
      title: t('Something went wrong'),
      subtitle: t('Please try again later.'),
      icon: <AlertOutlinedIcon />,
    },
  }
  const error = errorCode && errorMap[errorCode]

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <AppBar className={classes.appBar} position="fixed" elevation={0} color="default" id="benla">
        <Toolbar
          style={{
            backgroundColor: tokens.backgroundColorSecondary,
            paddingLeft: `${tokens.spacing04}px !important`,
            paddingRight: `${tokens.spacing04}px !important`,
          }}
          variant="dense"
        >
          <div style={{ height: 34, display: 'flex', alignItems: 'center' }}>
            <GotoLogo>{chameleonTheme === 'dark' ? <GoToAdminDark /> : <GoToAdminLight />}</GotoLogo>
          </div>
        </Toolbar>
      </AppBar>

      <div style={{ padding: tokens.spacing04 }}>
        <EmptyState title={error?.title} actions={error?.action} icon={error?.icon}>
          {error?.subtitle}
        </EmptyState>
      </div>
    </div>
  )
}

export default ErrorPage
